import { Pipe, PipeTransform } from '@angular/core';
import moment from 'moment';
import 'moment-duration-format';
import { getActiveLocale } from '@nimbus-library/features/localization/utils/active-locale';

const HUMAN_READABLE_UNITS = ['day', 'hour', 'minute', 'second'];
const unitFormats = new Map<string, Intl.NumberFormat>();

function getUnitFormat(locale: string, unit: string): Intl.NumberFormat {
  const key = `${locale}|${unit}`;
  if (!unitFormats.has(key)) {
    unitFormats.set(key, new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'narrow' }));
  }
  return unitFormats.get(key);
}

@Pipe({
  name: 'durationFormat',
  standalone: false,
})
export class DurationFormatPipe implements PipeTransform {
  public transform(input: moment.Duration | string, format: string, nullPlaceholder?: string): any {
    if (!input) {
      return nullPlaceholder ? nullPlaceholder : '';
    }

    let duration = moment.isDuration(input) ? input : moment.duration(input);

    if (nullPlaceholder && duration.asSeconds() === 0) {
      return nullPlaceholder;
    }

    if (format === 'round-up') {
      if (duration.asMilliseconds() < 1000) {
        duration = moment.duration(1, 'second');
      }
      format = 'auto';
    }
    if (format === 'auto') {
      format = 'mm:ss';
      if (duration.asHours() >= 1) {
        format = `hh:${format}`;
      }
      if (duration.asDays() >= 1) {
        format = `dd:${format}`;
      }
    }
    if (format === 'time-in-state') {
      format = 'mm:ss';
      if (duration.asHours() >= 1) {
        format = `h:${format}`;
      }
    }
    if (format === 'human-readable') {
      return this.formatHumanReadable(duration);
    }

    return (<any>duration).format(format, { trim: false });
  }

  private formatHumanReadable(duration: moment.Duration): string {
    let format = 's';
    if (duration.asMinutes() >= 1) {
      format = `m:${format}`;
    }
    if (duration.asHours() >= 1) {
      format = `h:${format}`;
    }
    if (duration.asDays() >= 1) {
      format = `d:${format}`;
    }
    const values = duration.format(format, { trim: false, useGrouping: false }).split(':').map(Number);
    const units = HUMAN_READABLE_UNITS.slice(HUMAN_READABLE_UNITS.length - values.length);
    const locale = getActiveLocale();

    return values.map((value, index) => getUnitFormat(locale, units[index]).format(value)).join(' ');
  }
}
